import { Col, Row } from "react-grid-system";
import { StyledDecisionOverview, StyledMoreActionsTitle } from "../../../styles/StyledDecision/StyledContent";
import { editOverviewTab, getOverviewTab, lockOverviewTab } from "../../../api/decision/tabs";
import { useEffect, useRef, useState } from "react";

import Editor from "ckeditor5-custom-build/build/ckeditor";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import { Button } from "../../Button";
import Modal from "react-modal";
import { ModalHeader } from "../../ModalHeader";
import { MoreActions } from "../../MoreActions";
import PropTypes from "prop-types";
import SaveIcon from "../../../assets/img/icons/save.svg";
import { StyledModalTextarea } from "../../../styles/StyledModalTextarea";
import { useQuery } from "react-query";
import { useDecisionContext } from "../../../context/decisionContext";
import { DecisionLink } from ".";
import { QueryLoader } from "../../Loader";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import parse from "html-react-parser";
import { StyledDescriptionModal } from "../../../styles/StyledModal";

const editorConfiguration = {
	toolbar: ["bold", "italic", "link", "bulletedList", "numberedList", "undo", "redo"],
};

export default function DecisionOverview({ id, edit, test = false, newDecision = false, prefillData = null }) {
	const contextData = useDecisionContext();
	
	// Handle case where context is not available (but allow for new decisions)
	if (!contextData && !newDecision) {
		return <div>Loading decision data...</div>;
	}
	
	const { status, unit } = contextData || {};
	const axiosHandler = useAxiosPrivate();

	// For new decisions with prefill data, use that instead of API call
	// But for existing decisions, always use API even in edit mode
	const shouldUseAPI = !newDecision; // Always use API for existing decisions
	
	const { isLoading, isError, error, data, refetch } = shouldUseAPI
		? useQuery(["decisionTab", id], () => getOverviewTab(axiosHandler.axiosPrivate, id), {
			refetchOnMount: true, // Always refetch when component mounts
			refetchOnWindowFocus: false,
			staleTime: 0, // Consider data immediately stale
		})
		: { 
			isLoading: false, 
			isError: false, 
			error: null, 
			data: { 
				basisDescription: prefillData?.description || "", 
				decisionDescription: prefillData?.summary || "" 
			} 
		};

	const [openDropdowns, setOpenDropdowns] = useState({ basis: false, decision: false });

	//DESCRIPTIONS - Initialize with prefill data if available
	const [basisDescription, setBasisDescription] = useState(
		newDecision ? (prefillData?.description || "") : ""
	);
	const [isBasisLimited, setIsBasisLimited] = useState(false);
	const basisRef = useRef(null);
	const [decisionDescription, setDecisionDescription] = useState(
		newDecision ? (prefillData?.summary || "") : ""
	);
	const [isDecisionLimited, setIsDecisionLimited] = useState(false);
	const decisionRef = useRef(null);

	//FOR MODAL
	const [modalIsOpen, setIsOpen] = useState(false);
	const [activeDescription, setActiveDescription] = useState("basis");
	const [text, setText] = useState("");

	useEffect(() => {
		Modal.setAppElement("body");
		
		// For existing decisions, set descriptions from API data
		if (!newDecision && !isLoading && !isError && data) {
			setBasisDescription(data.basisDescription ? data.basisDescription : "");
			setDecisionDescription(data.decisionDescription ? data.decisionDescription : "");
		}
		
		// For new decisions, ensure prefill data is loaded immediately
		if (newDecision && prefillData) {
			setBasisDescription(prefillData.description || "");
			setDecisionDescription(prefillData.summary || "");
		}
	}, [isLoading, data, newDecision, prefillData]);

	useEffect(() => {
		if (!isLoading && !isError && !edit) {
			if (basisRef.current && basisRef.current.clientHeight >= 268.8) {
				setIsBasisLimited(true);
			}
			if (decisionRef.current && decisionRef.current.clientHeight >= 268.8) {
				setIsDecisionLimited(true);
			}
		}
	}, [basisDescription, decisionDescription]);

	const handleDataUpdate = async (id, result) => {
		if (newDecision) {
			// For new decisions, store the data temporarily in sessionStorage
			const existingData = JSON.parse(sessionStorage.getItem("prefillDecisionData") || "{}");
			const updatedData = {
				...existingData,
				description: result.basisDescription !== undefined ? result.basisDescription : existingData.description,
				summary: result.decisionDescription !== undefined ? result.decisionDescription : existingData.summary,
			};
			sessionStorage.setItem("prefillDecisionData", JSON.stringify(updatedData));
			
			// Update local state to reflect changes immediately
			if (result.basisDescription !== undefined) {
				setBasisDescription(result.basisDescription);
			}
			if (result.decisionDescription !== undefined) {
				setDecisionDescription(result.decisionDescription);
			}
		} else {
			await editOverviewTab(axiosHandler.axiosPrivate, refetch, id, result);
		}
	};

	const handleLockEdition = async (id, data) => {
		await lockOverviewTab(axiosHandler.axiosPrivate, refetch, id, { content: data });
	};

	const openModal = (type) => {
		setActiveDescription(type);
		setText(type === "basis" ? basisDescription : decisionDescription);
		setIsOpen(true);
	};

	const closeModal = () => {
		setIsOpen(false);
	};

	const isDone = status?.slug !== "ongoing";
	// For new decisions, assume they're not done and editing is allowed
	const isNewDecisionEditable = newDecision;

	if (isLoading) {
		return <QueryLoader />;
	}
	if (isError) {
		return <div>{error}</div>;
	}

	// Allow rendering for new decisions even without full context
	if ((status && unit) || newDecision)
		return (
			<>
				<StyledDecisionOverview role="decision-overivew">
					<Col sm={12} lg={6}>
						<Row>
							<StyledMoreActionsTitle>
								<MoreActions
									type="basis"
									options={[
										{
											display: true,
											click:
												(isNewDecisionEditable || (!isDone && !data.blockBasis))
													? () => {
														openModal("basis");
													  }
													: () => {},
											testId: "basis-edit",
											name: "Edit basis",
										},
										{
											display: !newDecision && !isDone && !data.blockBasis,
											click: () => {
												handleLockEdition(id, "blockBasis");
											},
											testId: "basis-lock",
											name: "Lock edition",
										},
										{
											display: !newDecision,
											click: () => {},
											testId: "basis-log",
											name: "Basis log",
										},
									]}
									isOpen={openDropdowns}
									setIsOpen={setOpenDropdowns}
									isDisabled={!isNewDecisionEditable && (isDone || data.blockBasis)}
								/>
								<h2>Basis</h2>
							</StyledMoreActionsTitle>
						</Row>
						<Row>
							<Col>
								<div
									ref={basisRef}
									className={`desc ${isBasisLimited && "limited"}`}
									role="basis-description"
									data-testid="basis-description"
								>
									{basisDescription !== "" ? parse(basisDescription) : "No basis description found"}
								</div>
								{isBasisLimited && (
									<Button onClick={() => setIsBasisLimited(false)} bg={false} small={true}>
										Load more
									</Button>
								)}
							</Col>
						</Row>
					</Col>
					<Col sm={12} lg={6}>
						<Row>
							<StyledMoreActionsTitle>
								<MoreActions
									type="decision"
									options={[
										{
											display: true,
											click:
												(isNewDecisionEditable || (!isDone && !data.blockDecision))
													? () => {
														openModal("decision");
													  }
													: () => {},
											testId: "decision-edit",
											name: "Edit decision",
										},
										{
											display: !newDecision && !isDone && !data.blockDecision,
											click: () => {
												handleLockEdition(id, "blockDecision");
											},
											testId: "decision-lock",
											name: "Lock edition",
										},
										{
											display: !newDecision,
											click: () => {},
											testId: "decision-log",
											name: "Decision log",
										},
									]}
									isOpen={openDropdowns}
									setIsOpen={setOpenDropdowns}
									isDisabled={!isNewDecisionEditable && (isDone || data.blockDecision)}
								/>
								<h2>Decision</h2>
							</StyledMoreActionsTitle>
						</Row>
						<Row>
							<Col>
								<div
									ref={decisionRef}
									className={`desc ${isDecisionLimited && "limited"}`}
									role="decision-description"
									data-testid="decision-description"
								>
									{decisionDescription !== "" ? parse(decisionDescription) : "No decision description found"}
								</div>
								{isDecisionLimited && (
									<Button onClick={() => setIsDecisionLimited(false)} bg={false} small={true}>
										Load more
									</Button>
								)}
							</Col>
						</Row>
					</Col>
					<Col sm={12}>
						{!newDecision && status?.slug && (
							<DecisionLink id={id} status={status.slug} url={data?.url} refetch={refetch} />
						)}
						{newDecision && (
							<div style={{ padding: "10px", backgroundColor: "#f0f8ff", borderRadius: "4px", fontStyle: "italic" }}>
								Decision link will be available after saving the decision
							</div>
						)}
					</Col>
				</StyledDecisionOverview>
				<Modal
					isOpen={modalIsOpen}
					onRequestClose={closeModal}
					style={StyledDescriptionModal}
					appElement={document.getElementById("app")}
					role="overviewEdit-modal"
				>
					<ModalHeader onClick={() => closeModal()} data-testid="overviewEdit-modalHeader">
						{activeDescription === "basis" ? "Edit Basis" : "Edit Decision"}
					</ModalHeader>
					<StyledModalTextarea>
						<CKEditor
							editor={Editor}
							config={editorConfiguration}
							data={text}
							onChange={(event, editor) => {
								const data = editor.getData();
								setText(data);
							}}
						/>
						<div>
							<Button testid="overviewEdit-cancelButton" onClick={() => closeModal()} bg={false} small={true}>
								cancel
							</Button>
							<Button
								icon={SaveIcon}
								testid="overviewEdit-submitButton"
								onClick={() => {
									activeDescription === "basis" ? setBasisDescription(text) : setDecisionDescription(text);
									const result = activeDescription === "basis" ? { basisDescription: text } : { decisionDescription: text };
									closeModal();
									handleDataUpdate(id, result);
								}}
							>
								save changes
							</Button>
						</div>
					</StyledModalTextarea>
				</Modal>
			</>
		);

	return <></>;
}

DecisionOverview.propTypes = {
	id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	test: PropTypes.bool,
	status: PropTypes.object,
	edit: PropTypes.bool,
	newDecision: PropTypes.bool,
	prefillData: PropTypes.object,
};
